/**
 * Plain-English sentences for workspaces and jobs.
 *
 * A researcher must be able to read one line and know what is happening. No
 * Kubernetes vocabulary appears here, and nothing is asserted that the API did
 * not report.
 */

import type { Job, JobState, Workspace, WorkspaceState } from "../api/types";
import { TERMINAL_WORKSPACE_STATES } from "../api/types";
import { formatElapsed, formatRemaining, pluralize } from "./format";

/** How a state should feel: neutral, good, urgent, broken or finished. */
export type Tone = "pending" | "ok" | "warning" | "danger" | "done";

const WORKSPACE_TONES: Record<WorkspaceState, Tone> = {
  waiting_for_node: "pending",
  starting: "pending",
  ready: "ok",
  expiring: "warning",
  stopped: "done",
  expired: "done",
  failed: "danger",
};

const JOB_TONES: Record<JobState, Tone> = {
  waiting_for_node: "pending",
  starting: "pending",
  running: "ok",
  succeeded: "done",
  failed: "danger",
  cancelled: "done",
  expired: "done",
};

/**
 * Tone of a workspace, state first but not state alone.
 *
 * A workspace on an idle countdown reads `ready` and is minutes from being
 * stopped; showing it in the same calm green as one nobody is about to touch is
 * how a researcher walks away and loses it.
 *
 * Args:
 *     workspace: The workspace, or just its state.
 *
 * Returns:
 *     The tone to render it in.
 */
export function workspaceTone(workspace: Workspace | WorkspaceState): Tone {
  if (typeof workspace === "string") {
    return WORKSPACE_TONES[workspace];
  }
  if (isIdleAtRisk(workspace)) {
    return "warning";
  }
  return WORKSPACE_TONES[workspace.state];
}

/**
 * True when the console has started an idle countdown on this workspace.
 *
 * Args:
 *     workspace: The workspace as served by the API.
 *
 * Returns:
 *     Whether it will be stopped for doing nothing unless something changes.
 */
export function isIdleAtRisk(workspace: Workspace): boolean {
  return (
    workspace.idle_stops_at !== null &&
    (workspace.state === "ready" || workspace.state === "expiring")
  );
}

/**
 * Whether nothing moves for this workspace any more.
 *
 * A workspace that has stopped, expired or failed keeps its `expires_at`: the
 * field is history, not a countdown, and a screen that reads the field instead
 * of the state tells somebody their ended workspace "stops in 7 h 49". One
 * source of truth for the list — `TERMINAL_WORKSPACE_STATES`.
 *
 * Args:
 *     state: The workspace state as served by the API.
 *
 * Returns:
 *     Whether no clock counts down to anything for it any more.
 */
export function isTerminalWorkspaceState(state: WorkspaceState): boolean {
  return TERMINAL_WORKSPACE_STATES.includes(state);
}

/** Tone of a job state. */
export function jobTone(state: JobState): Tone {
  return JOB_TONES[state];
}

/**
 * Short state word for badges, e.g. `Ready`, `Waiting`.
 *
 * Args:
 *     workspace: The workspace, or just its state.
 *
 * Returns:
 *     One or two words for the badge.
 */
export function workspaceStateLabel(
  workspace: Workspace | WorkspaceState,
): string {
  if (typeof workspace !== "string") {
    return isIdleAtRisk(workspace)
      ? "Unused"
      : workspaceStateLabel(workspace.state);
  }
  const state = workspace;
  switch (state) {
    case "waiting_for_node":
      return "Waiting";
    case "starting":
      return "Starting";
    case "ready":
      return "Ready";
    case "expiring":
      return "Expiring";
    case "stopped":
      return "Stopped";
    case "expired":
      return "Expired";
    case "failed":
      return "Failed";
  }
}

/** Short state word for a job. */
export function jobStateLabel(state: JobState): string {
  switch (state) {
    case "waiting_for_node":
      return "Waiting";
    case "starting":
      return "Starting";
    case "running":
      return "Running";
    case "succeeded":
      return "Finished";
    case "failed":
      return "Failed";
    case "cancelled":
      return "Cancelled";
    case "expired":
      return "Out of time";
  }
}

/**
 * The one sentence that explains a workspace.
 *
 * Everything it says is read off the workspace: whether a machine was really
 * requested, how many people are ahead, whether there is a log to read. The
 * state alone is not enough — `waiting_for_node` is reached both when a machine
 * is on its way and when none can be started, and telling somebody to close the
 * page in the second case is how they come back to a dropped request.
 *
 * Args:
 *     workspace: The workspace as served by the API.
 *     now: Reference instant in milliseconds (injectable for tests).
 *
 * Returns:
 *     A sentence a researcher can act on, with no jargon.
 */
export function workspaceHeadline(
  workspace: Workspace,
  now: number = Date.now(),
): string {
  const gpus = pluralize(workspace.gpus, "GPU");
  const verb = workspace.gpus === 1 ? "is" : "are";
  if (isIdleAtRisk(workspace) && workspace.idle_stops_at) {
    return (
      `Your ${gpus} ${verb} doing nothing, so this workspace stops in ` +
      `${formatRemaining(workspace.idle_stops_at, now)} and the GPUs go back ` +
      `to the pool. Use them, or add time, and the countdown disappears.`
    );
  }
  switch (workspace.state) {
    case "waiting_for_node":
      return waitingHeadline(workspace, gpus, now);
    case "starting":
      return startingHeadline(workspace);
    case "ready":
      return workspace.expires_at
        ? `Ready. ${gpus} ${verb} yours for another ${formatRemaining(workspace.expires_at, now)}.`
        : `Ready. ${gpus} ${verb} yours.`;
    case "expiring":
      return `This workspace stops in ${formatRemaining(workspace.expires_at, now)}. Extend it if you still need it.`;
    case "stopped":
      return "Stopped. Everything you saved in your home folder is still there.";
    case "expired":
      return "The time you asked for ran out, so the workspace was stopped. Everything you saved in your home folder is still there.";
    case "failed":
      return failedHeadline(workspace);
  }
}

/**
 * What is true about a workspace that has no machine yet.
 *
 * The server's sentence when there is one, and nothing else: it is the only
 * party that knows the lane, the machine being started for the request ahead
 * and the reason no machine could be started. This file knows none of the
 * three, which is how it once told somebody that no machine was being started
 * while one was booting for the request in front of her.
 *
 * What follows is the fallback for a bundle newer than the API it is talking
 * to — production runs exactly that pair between two deploys — and it is the
 * wording this page carried before `waiting` existed.
 */
function waitingHeadline(
  workspace: Workspace,
  gpus: string,
  now: number,
): string {
  if (workspace.waiting) {
    return workspace.waiting.message;
  }
  const queue =
    workspace.queue_position && workspace.queue_position > 1
      ? ` ${workspace.queue_position - 1} other ${
          workspace.queue_position === 2 ? "request is" : "requests are"
        } ahead of yours.`
      : "";
  if (workspace.machine_requested_at === null) {
    // Deliberately not "no GPU is free": a request is also in this state for
    // the few seconds between asking and being placed, and the same screen
    // shows the measured number of free GPUs right underneath. What is always
    // true — and what the researcher must not miss — is that no machine is
    // being started for them, so waiting only ends if somebody releases GPUs.
    return (
      `Waiting for a machine. No new machine is being started for this ` +
      `request, so your ${gpus} open as soon as GPUs are free.${queue} If ` +
      `none free up, the request is dropped rather than left hanging.`
    );
  }
  return (
    `No GPU was free, so a machine was started for you ` +
    `${formatElapsed(workspace.machine_requested_at, now)} ago. Your ${gpus} ` +
    `open by themselves as soon as it joins — you can close this page.${queue}`
  );
}

/**
 * What is true about a workspace the console has handed to the cluster.
 *
 * "Starting" covers two very different situations: the machine is pulling the
 * environment, and no machine will take the request at all — a node with free
 * GPUs but no CPU or memory left, a taint, a full disk. The second one used to
 * read as the first, and a researcher watched "a minute or two" for half an
 * hour. When the cluster gives a reason, it is the headline.
 */
function startingHeadline(workspace: Workspace): string {
  if (workspace.error) {
    const reason = workspace.error.trim().replace(/\.$/, "");
    return (
      `Your workspace has not started yet: ${reason}. The console keeps ` +
      `trying; stop it and ask for fewer GPUs if it does not clear.`
    );
  }
  return workspace.node
    ? `Your workspace is starting on ${workspace.node} — usually a minute or two while the environment is downloaded.`
    : `Your request is with the cluster, waiting for a machine to take it — usually seconds.`;
}

/**
 * Why a workspace failed, pointing only at things that exist.
 *
 * The split is on `started_at`, not on the state: `failed` is reached both by a
 * workspace whose environment never arrived and by one that ran for two hours
 * and was then stopped. Telling the second person it "could not start" is the
 * sentence that sent a researcher looking through a startup log for a program
 * that had been running all afternoon.
 *
 * Within the never-started half, `node` is the second split: a container that
 * started, crashed and was ended before it was ever usable has no `started_at`
 * but did reach a machine, and only a workspace with no machine at all is told
 * it never reached one.
 *
 * Known limitation, deliberately not coded around: a resize does not clear

 * `started_at`, so a resize whose replacement cannot start reads as "This
 * workspace stopped: …". The server's own `error` is still the never-started
 * clause in that case, so the sentence stays true, and the resize line directly
 * above it in the trace says the environment was being replaced.
 */
function failedHeadline(workspace: Workspace): string {
  if (workspace.started_at === null) {
    if (workspace.error) {
      const reason = workspace.error.trim().replace(/\.$/, "");
      return workspace.has_logs
        ? `This workspace could not start: ${reason}. The startup log below says more.`
        : `This workspace could not start: ${reason}. Nothing was lost — ask again.`;
    }
    if (workspace.has_logs) {
      return "This workspace could not start. The startup log below says why.";
    }
    // "It never reached a machine" is only true of a workspace that never had
    // one. A container that started, crashed and was ended before it was ever
    // usable has no `started_at` either, and telling its owner it never
    // reached a machine — one line under a trace saying what exit code it
    // stopped with — is the false sentence this file exists to remove.
    return workspace.node === null
      ? "This workspace could not start, and it never reached a machine, so there is no log to read. Nothing was lost — ask again."
      : "This workspace could not start. The machine it was on has been given back, so there is no log left to read. Nothing was lost — ask again.";
  }

  const reason = workspace.error?.trim().replace(/\.$/, "") ?? null;
  if (reason) {
    return workspace.has_logs
      ? `This workspace stopped: ${reason}. The log below is the last thing it wrote, and your files in /home are still there.`
      : `This workspace stopped: ${reason}. Your files in /home are still there.`;
  }
  return workspace.has_logs
    ? "This workspace stopped. The log below is the last thing it wrote, and your files in /home are still there."
    : "This workspace stopped. Your files in /home are still there.";
}

/**
 * The one sentence that explains a job.
 *
 * Args:
 *     job: The job as served by the API.
 *     now: Reference instant in milliseconds (injectable for tests).
 *
 * Returns:
 *     A sentence a researcher can act on.
 */
export function jobHeadline(job: Job, now: number = Date.now()): string {
  switch (job.state) {
    case "waiting_for_node":
      // The server's sentence when it sent one; the old wording is what a
      // bundle newer than its API falls back to.
      return job.waiting
        ? job.waiting.message
        : "Waiting for a machine with a free GPU.";
    case "starting":
      return "Starting.";
    case "running":
      return job.expires_at
        ? `Running — it will be stopped in ${formatRemaining(job.expires_at, now)} if it has not finished.`
        : "Running.";
    case "succeeded":
      return "Finished successfully.";
    case "failed":
      return job.exit_code !== null && job.exit_code !== undefined
        ? `Failed — the command exited with code ${job.exit_code}.`
        : "Failed. The log says why.";
    case "cancelled":
      return "You cancelled this job.";
    case "expired":
      return "Stopped: it ran longer than the time you asked for.";
  }
}
